// /verify?token=XXX — calls backend, auto-signs the user in, redirects to /projects.

import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { useAuth, formatApiError } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "zamba_token";

export default function Verify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const ranRef = useRef(false);
  const [state, setState] = useState({ status: "verifying", message: "" });

  // Resend form (shown on failure)
  const [resendEmail, setResendEmail] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [resendInfo, setResendInfo] = useState("");

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    const token = searchParams.get("token");
    if (!token) {
      setState({ status: "error", message: "Missing verification token." });
      return;
    }
    axios
      .post(`${API}/auth/verify`, { token })
      .then(({ data }) => {
        // Auto sign-in
        sessionStorage.setItem(TOKEN_KEY, data.token);
        // Optimistic context refresh — easiest is a hard reload of the AuthContext
        // by setting state explicitly via a small refetch:
        return axios.get(`${API}/auth/me`).then((r) => {
          // Reflect into AuthContext via custom event handled below — simpler:
          // just navigate; AuthProvider will pick up the token on next mount.
          setState({ status: "success", message: r.data.email });
          // Tiny delay so user sees the success state, then redirect.
          setTimeout(() => {
            // Force a full app re-render so AuthProvider re-validates the token
            window.location.href = "/home";
          }, 1200);
        });
      })
      .catch((err) => {
        setState({
          status: "error",
          message: formatApiError(err),
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResendBusy(true);
    setResendInfo("");
    try {
      const { data } = await axios.post(`${API}/auth/resend-verification`, {
        email: resendEmail.trim(),
      });
      setResendInfo(
        data.email_sent
          ? "If your email is in our system and unverified, a new link has been sent."
          : data.verification_link_dev
            ? `Email service not configured. Link (dev): ${data.verification_link_dev}`
            : "If your email is in our system and unverified, a new link has been sent."
      );
    } catch (err) {
      setResendInfo(formatApiError(err));
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-slate-50 to-slate-200">
      <Card className="w-full max-w-md shadow-lg" data-testid="verify-card">
        <CardHeader>
          <CardTitle className="text-xl">Email Verification</CardTitle>
        </CardHeader>
        <CardContent>
          {state.status === "verifying" && (
            <div className="py-6 flex flex-col items-center gap-3" data-testid="verify-pending">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              <div className="text-sm text-slate-600">Verifying your account…</div>
            </div>
          )}

          {state.status === "success" && (
            <div className="py-6 flex flex-col items-center gap-3" data-testid="verify-success">
              <CheckCircle2 className="h-10 w-10 text-emerald-600" />
              <div className="text-base font-medium">Verified</div>
              <div className="text-sm text-slate-600 text-center">
                Welcome aboard. Redirecting you to your projects…
              </div>
            </div>
          )}

          {state.status === "error" && (
            <div className="space-y-4" data-testid="verify-error">
              <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded p-3">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" />
                <div>{state.message || "Verification failed."}</div>
              </div>

              <form onSubmit={doResend} className="space-y-2" data-testid="verify-resend-form">
                <div className="text-sm text-slate-600">
                  Enter your email to receive a fresh verification link:
                </div>
                <Input
                  type="email"
                  required
                  placeholder="you@yourcompany.com"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  data-testid="verify-resend-email"
                />
                <Button
                  type="submit"
                  disabled={resendBusy}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="verify-resend-submit"
                >
                  {resendBusy ? "Sending…" : "Send new verification email"}
                </Button>
                {resendInfo && (
                  <div
                    className="text-xs text-slate-600 break-all"
                    data-testid="verify-resend-info"
                  >
                    {resendInfo}
                  </div>
                )}
              </form>

              <div className="text-center text-sm text-slate-500">
                <Link to="/login" className="underline hover:text-slate-800">
                  Back to sign in
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
